// src/contexts/SidePanelContext.tsx

import React, { createContext, useContext, useState, ReactNode } from 'react';
import { Drawer, Box, Typography, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

interface SidePanelContextType {
  isOpen: boolean;
  showPanel: (
    title: string,
    content: ReactNode,
    footer?: ReactNode,
    forceWidth?: number | undefined,
    close?: Function,
  ) => void;
  hidePanel: () => void;
  CustomDrawer: React.FC<CustomDrawerProps>;
}

export const SidePanelContext = createContext<SidePanelContextType | undefined>(
  undefined,
);

interface SidePanelProviderProps {
  children: ReactNode;
  close?: Function;
}

interface CustomDrawerProps {
  content: ReactNode;
  footer: ReactNode;
  hidePanel: () => void;
  isOpen: boolean;
  title: string;
  width?: number;
  close?: Function;
}

const CustomDrawer: React.FC<CustomDrawerProps> = ({
  content,
  footer,
  hidePanel,
  isOpen,
  title,
  width = 375,
}) => {
  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={hidePanel}
      sx={{
        '& .MuiDrawer-paper': {
          zIndex: 1150, // Lower than both drawer and bottom navigation
        },
      }}
    >
      <Box data-testid="sideMenu" sx={{ width: width, overflowX: 'hidden' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
            borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
            backgroundColor: '#f0f0f0', // Slightly darker background for the header
          }}
        >
          <Typography
            sx={{ color: '#1A1A1E', fontSize: '24px', fontWeight: '500' }}
          >
            {title}
          </Typography>
          <IconButton onClick={hidePanel} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <Box>{content}</Box>
      </Box>
      {footer && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            width: 375,
            p: '12px 12px 16px',
            borderTop: '1px solid',
            borderColor: 'grey.300',
            backgroundColor: '#f0f0f0',
          }}
        >
          {footer}
        </Box>
      )}
    </Drawer>
  );
};

export const SidePanelProvider: React.FC<SidePanelProviderProps> = ({
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [content, setContent] = useState<ReactNode | null>(null);
  const [footer, setFooter] = useState<ReactNode | null>(null);
  const [title, setTitle] = useState<string>('');
  const [width, setWidth] = useState<number>(375);
  const [close, setCloseFunction] = useState<Function>(() => {});

  const showPanel = (
    newTitle: string,
    newContent: ReactNode,
    newFooter: ReactNode | undefined,
    forceWidth: number | undefined = 375,
    close: Function | undefined = () => {},
  ) => {
    setTitle(newTitle);
    setContent(newContent);
    setFooter(newFooter);
    setIsOpen(true);
    if (typeof forceWidth === 'number') {
      setWidth(forceWidth);
    }
    if (typeof close === 'function') {
      setCloseFunction(() => close);
    }
  };

  const hidePanel = () => {
    setIsOpen(false);
    if (close) {
      close();
    }
  };

  return (
    <SidePanelContext.Provider
      value={{ isOpen, showPanel, hidePanel, CustomDrawer }}
    >
      {children}
      <CustomDrawer
        title={title}
        content={content}
        isOpen={isOpen}
        hidePanel={hidePanel}
        footer={footer}
        width={width}
      />
    </SidePanelContext.Provider>
  );
};

export const useSidePanel = () => {
  const context = useContext(SidePanelContext);
  if (context === undefined) {
    throw new Error('useSidePanel must be used within a SidePanelProvider');
  }
  return context;
};
